/**
 * Book Value Report — Admin Page
 *
 * Per-broker outstanding loan book balances with month-over-month trend.
 * Generates a shareable read-only link for external recipients (parent groups,
 * acquirers, partners).
 *
 * RBAC: super_admin + administrator. Gated by per-tenant
 * featureFlags.book_value_report ('off' | 'observe' | 'enabled').
 *
 * Source data: monthly XLSX uploaded by founder via
 *   services/admin-functions/scripts/upload-book-value-xlsx.ts
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import {
  Vault, Share2, X, Copy, Check, Loader2, Trash2, ExternalLink,
  ShieldCheck, ShieldAlert,
} from 'lucide-react';
import PageHeader from '../../components/common/PageHeader';
import { formatCurrency } from '../../utils/formatters';

type ReconStatus = 'matched' | 'mismatch' | 'no_data';

interface GenerateResponse {
  shareId: string;
  token: string;
  shareUrl: string;
  expiresAt?: string;
  reconciliationStatus: ReconStatus;
  visibleTotalCents: number;
}

const formatPeriodLabel = (periodMonth: string): string => {
  const [y, m] = periodMonth.split('-');
  return new Date(Number(y), Number(m) - 1).toLocaleDateString('en-AU', {
    month: 'long', year: 'numeric',
  });
};

const lastNMonthOptions = (n: number): string[] => {
  const out: string[] = [];
  const now = new Date();
  for (let i = 1; i <= n; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return out;
};

const ReconBadge: React.FC<{ status: ReconStatus }> = ({ status }) => {
  if (status === 'matched') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-emerald-500/15 text-emerald-400">
        <ShieldCheck className="w-3 h-3" /> Reconciled
      </span>
    );
  }
  if (status === 'mismatch') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-amber-500/15 text-amber-400">
        <ShieldAlert className="w-3 h-3" /> Drift
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-gray-500/15 text-gray-400">
      No data
    </span>
  );
};

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  result: GenerateResponse | null;
  isLoading: boolean;
  error: string | null;
  onRevoke: () => void;
  isRevoking: boolean;
}

const ShareModal: React.FC<ShareModalProps> = ({
  isOpen, onClose, result, isLoading, error, onRevoke, isRevoking,
}) => {
  const [copied, setCopied] = useState(false);
  if (!isOpen) return null;

  const handleCopy = async () => {
    if (result?.shareUrl) {
      await navigator.clipboard.writeText(result.shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 border border-white/[0.06] rounded-xl shadow-2xl max-w-xl w-full">
        <div className="flex justify-between items-start p-6 border-b border-white/[0.06]">
          <div>
            <h3 className="text-lg font-bold text-white">Share Book Value Report</h3>
            <p className="text-sm text-gray-400 mt-0.5">
              Recipient sees a read-only snapshot of the selected period
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
              <span className="ml-3 text-gray-300">Generating share link…</span>
            </div>
          )}

          {error && (
            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {result && !isLoading && (
            <>
              <div className="mb-4 p-3 bg-gray-900/40 border border-white/[0.04] rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
                    Total Visible Book
                  </span>
                  <ReconBadge status={result.reconciliationStatus} />
                </div>
                <div className="text-2xl font-bold text-white tabular-nums">
                  {formatCurrency(result.visibleTotalCents)}
                </div>
              </div>

              <label className="block text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2">
                Share URL
              </label>
              <div className="flex items-center gap-2 mb-3">
                <input
                  type="text"
                  readOnly
                  value={result.shareUrl}
                  onFocus={(e) => e.currentTarget.select()}
                  className="flex-1 px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm font-mono"
                />
                <button
                  onClick={handleCopy}
                  className="px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white flex items-center gap-1.5 transition-colors text-sm font-medium"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
                <a
                  href={result.shareUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white flex items-center gap-1.5 transition-colors text-sm font-medium"
                >
                  <ExternalLink className="w-4 h-4" /> Open
                </a>
              </div>

              {result.expiresAt && (
                <p className="text-xs text-gray-500 mb-4">
                  Link expires {new Date(result.expiresAt).toLocaleString('en-AU')}
                </p>
              )}

              <div className="flex justify-between items-center pt-3 border-t border-white/[0.06]">
                <span className="text-xs text-gray-500 font-mono">
                  ID: {result.shareId.substring(0, 8)}…
                </span>
                <button
                  onClick={onRevoke}
                  disabled={isRevoking}
                  className="inline-flex items-center gap-1.5 text-rose-400 hover:bg-rose-500/10 px-3 py-1.5 rounded-lg text-sm font-medium border border-rose-500/30 disabled:opacity-50 transition-colors"
                >
                  {isRevoking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  Revoke
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const BookValueReportPage: React.FC = () => {
  const { userData, companyData } = useOutletContext<{ userData: any; companyData: any }>();
  const companyId: string | undefined =
    companyData?.companyId || companyData?.id || userData?.companyId;

  const monthOptions = useMemo(() => lastNMonthOptions(12), []);
  const [periodMonth, setPeriodMonth] = useState<string>(monthOptions[0]);
  const [expiresInDays, setExpiresInDays] = useState<number>(7);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [result, setResult] = useState<GenerateResponse | null>(null);
  const [isRevoking, setIsRevoking] = useState(false);

  const handleGenerate = useCallback(async () => {
    if (!companyId) { setGenerateError('Missing company context'); return; }
    setIsModalOpen(true);
    setIsGenerating(true);
    setGenerateError(null);
    setResult(null);
    try {
      const callable = httpsCallable<
        { companyId: string; periodMonth: string; expiresInDays: number },
        GenerateResponse
      >(functions, 'generateBookValueReport');
      const r = await callable({ companyId, periodMonth, expiresInDays });
      setResult(r.data);
    } catch (e: any) {
      console.error('Generate failed:', e);
      setGenerateError(e?.message || String(e));
    } finally {
      setIsGenerating(false);
    }
  }, [companyId, periodMonth, expiresInDays]);

  const handleRevoke = useCallback(async () => {
    if (!companyId || !result?.shareId) return;
    if (!window.confirm('Revoke this share link? The recipient will no longer be able to view this report.')) return;
    setIsRevoking(true);
    try {
      const callable = httpsCallable<
        { companyId: string; shareId: string },
        { shareId: string; revokedAt: string }
      >(functions, 'revokeBookValueShare');
      await callable({ companyId, shareId: result.shareId });
      setResult(null);
      setIsModalOpen(false);
    } catch (e: any) {
      console.error('Revoke failed:', e);
      setGenerateError(e?.message || String(e));
    } finally {
      setIsRevoking(false);
    }
  }, [companyId, result]);

  if (!companyId) {
    return (
      <div className="space-y-6">
        <PageHeader icon={Vault} title="Book Value Report" subtitle="Per-broker outstanding loan book balances" />
        <div className="bg-amber-500/10 border border-amber-500/30 text-amber-300 px-4 py-3 rounded-lg text-sm">
          Loading company context…
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Vault}
        title="Book Value Report"
        subtitle="Per-broker outstanding loan book balances · share read-only with recipients"
        actions={
          <button
            onClick={handleGenerate}
            disabled={isGenerating}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg text-sm font-medium text-white transition-colors"
          >
            {isGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
            Generate share link
          </button>
        }
      />

      <div className="bg-gray-800/50 border border-white/[0.06] rounded-xl p-5">
        <h2 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">Report Settings</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2">Report Period</label>
            <select
              value={periodMonth}
              onChange={(e) => setPeriodMonth(e.target.value)}
              className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500"
              disabled={isGenerating}
            >
              {monthOptions.map((m) => (
                <option key={m} value={m}>{formatPeriodLabel(m)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2">Link Expires In</label>
            <select
              value={expiresInDays}
              onChange={(e) => setExpiresInDays(Number(e.target.value))}
              className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500"
              disabled={isGenerating}
            >
              <option value={1}>1 day</option>
              <option value={7}>7 days (recommended)</option>
              <option value={14}>14 days</option>
              <option value={30}>30 days</option>
              <option value={0}>No expiry</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={handleGenerate}
              disabled={isGenerating}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg text-sm font-medium text-white transition-colors"
            >
              {isGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
              {isGenerating ? 'Generating…' : 'Generate & share'}
            </button>
          </div>
        </div>
      </div>

      <div className="bg-gray-800/50 border border-white/[0.06] rounded-xl p-5">
        <h2 className="text-sm font-semibold text-white uppercase tracking-wider mb-3">About this report</h2>
        <ul className="space-y-2 text-sm text-gray-300">
          <li className="flex gap-2">
            <span className="text-blue-400 flex-shrink-0">·</span>
            <span>Shows <strong className="text-white">current outstanding balance per broker bucket</strong> with month-over-month trend, sourced from the latest book-value spreadsheet upload</span>
          </li>
          <li className="flex gap-2">
            <span className="text-blue-400 flex-shrink-0">·</span>
            <span>Each share link is a <strong className="text-white">snapshot</strong> — regenerate monthly with fresh data</span>
          </li>
          <li className="flex gap-2">
            <span className="text-blue-400 flex-shrink-0">·</span>
            <span>Cross-source reconciliation gate validates <strong className="text-white">90%+ of loans</strong> in the upload match VALUTTEN's BigQuery pipeline before the badge goes green</span>
          </li>
          <li className="flex gap-2">
            <span className="text-blue-400 flex-shrink-0">·</span>
            <span>Recipient sees a <strong className="text-white">read-only public page</strong> — no VALUTTEN login required, token-validated</span>
          </li>
        </ul>
      </div>

      <ShareModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        result={result}
        isLoading={isGenerating}
        error={generateError}
        onRevoke={handleRevoke}
        isRevoking={isRevoking}
      />
    </div>
  );
};

export default BookValueReportPage;
